import { isoDay, type Counts, type PermissionItem, type Task, type TaskEvent, type WsEvent } from '@jerome2/shared';
import { api } from '../api.ts';
import { createStore } from '../lib/store.ts';

// Taken (wachtrij), tellers en akkoordvragen. Gevuld bij het starten en via de websocket.

export interface TasksState {
  byId: Record<string, Task>;
  events: Record<string, TaskEvent[]>;
  counts: Counts;
  /** Zijn de tellers van de server er al (GET /api/mail/counts of de melding `counts`)? Tot dan zijn ze nul, niet echt. */
  countsLoaded: boolean;
  permissions: PermissionItem[];
  loaded: boolean;
}

export const tasksStore = createStore<TasksState>({ byId: {}, events: {}, counts: { flagged: 0, unread: 0, done: 0, question: 0, running: 0, failed: 0 }, countsLoaded: false, permissions: [], loaded: false });

export const ACTIVE_STATUSES = new Set(['queued', 'running', 'question', 'done', 'failed']);
export const BUSY_STATUSES = new Set(['queued', 'running']);
/** Taken die op Jeroen wachten: klaar met resultaat, of met een vraag. Die staan in Gevlagd/Ongelezen bovenaan onder "Klaar voor jou". */
export const READY_STATUSES = new Set(['done', 'question']);
/** Taken waardoor een mail uit Gevlagd/Ongelezen verdwijnt (hij staat dan bij Verwerkt). */
export const HIDING_STATUSES = new Set(['queued', 'running', 'question', 'done']);

/** Nooit een oudere stand over een nieuwere heen (een laat websocketbericht na een vers antwoord van de server). Bekijken (2.3.10)
 *  verandert `updatedAt` niet: bij een gelijke `updatedAt` wint een stand zonder `seenAt` niet van een stand met (seenAt gaat alleen
 *  terug op null met een nieuwe status, dus met een nieuwere `updatedAt`). */
function newer(cur: Task | undefined, t: Task): boolean {
  if (!cur || t.updatedAt > cur.updatedAt) return true;
  return t.updatedAt === cur.updatedAt && !(cur.seenAt && !t.seenAt);
}

export function upsertTask(task: Task): void {
  tasksStore.set((s) => (newer(s.byId[task.id], task) ? { ...s, byId: { ...s.byId, [task.id]: task } } : s));
}

export function upsertTasks(list: Task[]): void {
  tasksStore.set((s) => {
    const byId = { ...s.byId };
    for (const t of list) if (newer(byId[t.id], t)) byId[t.id] = t;
    return { ...s, byId, loaded: true };
  });
}

/** De bekeken taken van zoveel dagen staan in de werkerskolom onder "Klaar en bekeken" (2.3.10; de chip "7 dagen"). */
export const SEEN_DAYS = 7;
const DAY_MS = 86_400_000;
/** Hooguit zoveel taken per lijst voor de werkerskolom (de grens van `GET /api/tasks`). */
const BOARD_LIMIT = 500;

export async function loadActiveTasks(now: Date = new Date()): Promise<void> {
  // De akkoordvragen tegelijk met de taken: een mislukte vraag ernaar houdt de rest niet op. Sinds 2.3.10 ook voor de werkerskolom de
  // klare taken die de eigenaar nog niet opende (elke leeftijd) en de klare taken van de laatste zeven dagen.
  const since = new Date(now.getTime() - SEEN_DAYS * DAY_MS).toISOString();
  const list = (query: string) => api<{ tasks: Task[] }>(`/api/tasks?${query}`).then((r) => r.tasks);
  const [active, today, unseen, week, p] = await Promise.all([
    list('status=active'),
    list(`day=${isoDay(now)}`),
    // Deze twee zijn een hulp voor de kolom: mislukt er een, dan laden de taken en de akkoordvragen toch (de klare taken zitten ook in
    // status=active).
    list(`status=done,failed&unseen=1&limit=${BOARD_LIMIT}`).catch(() => [] as Task[]),
    list(`status=done,failed,finished,cancelled&since=${encodeURIComponent(since)}&limit=${BOARD_LIMIT}`).catch(() => [] as Task[]),
    api<{ permissions: PermissionItem[] }>('/api/tasks/permissions').catch(() => ({ permissions: [] as PermissionItem[] })),
  ]);
  const fresh = [...active, ...today, ...unseen, ...week];
  const ids = new Set(fresh.map((t) => t.id));
  tasksStore.set((s) => {
    const byId: Record<string, Task> = {};
    for (const t of Object.values(s.byId)) if (!ACTIVE_STATUSES.has(t.status) || ids.has(t.id)) byId[t.id] = t;
    for (const t of fresh) if (newer(byId[t.id], t)) byId[t.id] = t;
    return { ...s, byId, loaded: true, permissions: p.permissions };
  });
}

export async function loadTask(id: string): Promise<{ task: Task; events: TaskEvent[] }> {
  const r = await api<{ task: Task; events: TaskEvent[] }>(`/api/tasks/${encodeURIComponent(id)}`);
  tasksStore.set((s) => ({ ...s, byId: { ...s.byId, [r.task.id]: r.task }, events: { ...s.events, [r.task.id]: r.events } }));
  return r;
}

export async function loadCounts(opts: { background?: boolean } = {}): Promise<void> {
  const counts = await api<Counts>('/api/mail/counts', { background: opts.background ?? false });
  tasksStore.patch({ counts, countsLoaded: true });
}

export function applyTaskEvent(ev: WsEvent): void {
  if (ev.type === 'task.updated') upsertTask(ev.task);
  else if (ev.type === 'task.event') {
    tasksStore.set((s) => {
      const list = s.events[ev.event.taskId];
      if (!list) return s;
      if (list.some((e) => e.id === ev.event.id)) return s;
      return { ...s, events: { ...s.events, [ev.event.taskId]: [...list, ev.event] } };
    });
  } else if (ev.type === 'counts') tasksStore.patch({ counts: ev.counts, countsLoaded: true });
  else if (ev.type === 'permission') {
    tasksStore.set((s) => {
      const rest = s.permissions.filter((p) => p.id !== ev.permission.id);
      return { ...s, permissions: ev.permission.status === 'pending' ? [...rest, ev.permission] : rest };
    });
  }
}

const post = (id: string, action: string, body?: unknown) => api<Task>(`/api/tasks/${encodeURIComponent(id)}/${action}`, { method: 'POST', ...(body !== undefined ? { body } : {}) });

export const taskActions = {
  async answer(id: string, text: string) {
    upsertTask(await post(id, 'answer', { text }));
  },
  async steer(id: string, text: string) {
    upsertTask(await post(id, 'steer', { text }));
  },
  async retry(id: string) {
    upsertTask(await post(id, 'retry'));
  },
  async cancel(id: string) {
    upsertTask(await post(id, 'cancel'));
  },
  async finish(id: string) {
    upsertTask(await post(id, 'finish'));
  },
  async feedback(id: string, text: string) {
    await post(id, 'feedback', { text });
  },
  /** "Er klopt iets niet" bij een ondertekening: leerregel bewaren en de taak opnieuw laten lopen. */
  async signFeedback(id: string, text: string): Promise<{ text: string }> {
    const r = await api<{ task: Task; rule: { text: string } }>(`/api/sign/feedback/${encodeURIComponent(id)}`, { method: 'POST', body: { text } });
    upsertTask(r.task);
    return r.rule;
  },
  async approve(id: string) {
    const r = await api<{ ok: boolean; task: Task }>(`/api/tasks/${encodeURIComponent(id)}/approve`, { method: 'POST' });
    upsertTask(r.task);
  },
  /** Bekeken (2.3.10): de eigenaar opende het resultaat; de server zet `seenAt` één keer. Gebruik `markSeen`. */
  async seen(id: string) {
    upsertTask(await post(id, 'seen'));
  },
  async decidePermission(id: string, allow: boolean) {
    await api(`/api/tasks/permissions/${encodeURIComponent(id)}`, { method: 'POST', body: { allow } });
    tasksStore.set((s) => ({ ...s, permissions: s.permissions.filter((p) => p.id !== id) }));
  },
};

/** Volgorde van taken waar er één gekozen of bovenaan gezet wordt: bezig, wachtend, vraag, klaar, mislukt, afgerond, geannuleerd. */
const STATUS_RANK: Record<string, number> = { running: 0, queued: 1, question: 2, done: 3, failed: 4, finished: 5, cancelled: 6 };

/** De groepen van de werkerskolom (2.3.10, ticket 37198), van boven naar beneden. */
export interface WorkerGroups {
  /** Wachtend, bezig of met een vraag. */
  busy: Task[];
  /** Klaar of mislukt en nog niet geopend (zonder `seenAt`), hoe oud ook. */
  unseen: Task[];
  /** Klaar of mislukt en geopend, of afgerond of geannuleerd: bijgewerkt of bekeken in de laatste `SEEN_DAYS` dagen, of met `all` alles
   *  wat de client heeft. */
  seen: Task[];
}

const GROUP_BUSY = new Set(['queued', 'running', 'question']);
/** Klaar of mislukt: zonder `seenAt` "Klaar, nog niet bekeken". */
const SEEN_ABLE = new Set(['done', 'failed']);

const byRank = (a: Task, b: Task) => (STATUS_RANK[a.status] ?? 9) - (STATUS_RANK[b.status] ?? 9) || b.updatedAt.localeCompare(a.updatedAt);

export function groupTasks(state: TasksState, opts: { all: boolean; now?: Date }): WorkerGroups {
  const from = (opts.now ?? new Date()).getTime() - SEEN_DAYS * DAY_MS;
  const groups: WorkerGroups = { busy: [], unseen: [], seen: [] };
  for (const t of Object.values(state.byId)) {
    if (GROUP_BUSY.has(t.status)) groups.busy.push(t);
    else if (SEEN_ABLE.has(t.status) && !t.seenAt) groups.unseen.push(t);
    else if (opts.all || Math.max(Date.parse(t.updatedAt), t.seenAt ? Date.parse(t.seenAt) : 0) >= from) groups.seen.push(t);
  }
  groups.busy.sort(byRank);
  groups.unseen.sort(byRank);
  groups.seen.sort(byRank);
  return groups;
}

/** Het aantal taken dat wacht of bezig is: de teller "n bezig" in de kop, de strook en de bliksem op de telefoon (zoals vóór 2.3.10). */
export function busyTaskCount(state: TasksState): number {
  let n = 0;
  for (const t of Object.values(state.byId)) if (BUSY_STATUSES.has(t.status)) n += 1;
  return n;
}

const seenPending = new Set<string>();

/** Bekeken zetten als de eigenaar het resultaat van een werker opent (de rij in de werkerskolom, een item bij Verwerkt of onder Klaar
 *  voor jou dat hij zelf kiest; niet de keuze die de app zelf maakt):
 *  alleen bij een klare of mislukte taak zonder `seenAt`, en hooguit één verzoek per taak tegelijk. Een fout is stil: de volgende keer
 *  openen probeert het opnieuw. */
export async function markSeen(task: Task): Promise<void> {
  if (!SEEN_ABLE.has(task.status) || task.seenAt || seenPending.has(task.id)) return;
  seenPending.add(task.id);
  try {
    await taskActions.seen(task.id);
  } catch {
    // Bekeken is een hulp voor de kolom; een mislukt verzoek houdt het openen niet op.
  } finally {
    seenPending.delete(task.id);
  }
}

/** De taak die bij een mail hoort als er maar één getoond wordt: dezelfde rang als de server (`latestOf`), daarbinnen de nieuwste. */
export function latestTaskForMessage(state: TasksState, messageId: string): Task | null {
  let best: Task | null = null;
  const rank = (t: Task) => STATUS_RANK[t.status] ?? 9;
  for (const t of Object.values(state.byId)) {
    if (t.messageId !== messageId) continue;
    if (!best || rank(t) < rank(best) || (rank(t) === rank(best) && t.createdAt > best.createdAt)) best = t;
  }
  return best;
}

/** Andere open taken van dezelfde mail; Klaar haalt de vlag pas weg bij de laatste. */
export function openSiblings(state: TasksState, messageId: string, excludeId: string): number {
  let n = 0;
  for (const t of Object.values(state.byId)) if (t.messageId === messageId && t.id !== excludeId && ACTIVE_STATUSES.has(t.status)) n += 1;
  return n;
}
